import React, { useState } from 'react';
import { useAuth } from '../Services/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import './Login.scss';

const API_URL = process.env.REACT_APP_API_URL;

const Login = () => {
    const { login } = useAuth();
    const navigate = useNavigate();
    const intl = useIntl();
    const t = (id) => intl.formatMessage({ id });

    const [form, setForm] = useState({ identifier: '', password: '' });
    const [error, setError] = useState('');

    const [forgotEmail, setForgotEmail] = useState('');
    const [showForgotForm, setShowForgotForm] = useState(false);
    const [forgotStatus, setForgotStatus] = useState('idle');
    const [forgotMessage, setForgotMessage] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const res = await login(form);

        if (res.success) {
            navigate('/');
        } else {
            setError(t(res.errorId || 'login_failed'));
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setForgotStatus('sending');
        setForgotMessage('');

        try {
            const res = await fetch(`${API_URL}/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: forgotEmail.trim().toLowerCase() }),
            });

            if (!res.ok) {
                throw new Error(t('forgot_password_send_failed'));
            }

            setForgotStatus('success');
            setForgotMessage(t('forgot_password_sent'));
        } catch (err) {
            setForgotStatus('error');
            setForgotMessage(err.message || t('forgot_password_send_failed'));
        }
    };

    return (
        <div className="login-page">
            <h1>{t('login_heading')}</h1>

            {!showForgotForm ? (
                <>
                    <p>{t('login_intro')}</p>

                    <form onSubmit={handleSubmit}>
                        <div className="login-form">
                            <div className="inputs-row">
                                <input
                                    type="text"
                                    placeholder={t('login_identifier_placeholder')}
                                    value={form.identifier}
                                    onChange={(e) =>
                                        setForm({ ...form, identifier: e.target.value })
                                    }
                                    required
                                />

                                <input
                                    type="password"
                                    placeholder={t('password_placeholder')}
                                    value={form.password}
                                    onChange={(e) =>
                                        setForm({ ...form, password: e.target.value })
                                    }
                                    required
                                />
                            </div>

                            <div className="buttons-row">
                                <button type="submit">{t('login')}</button>

                                <button
                                    type="button"
                                    className="forgot-btn"
                                    onClick={() => {
                                        setShowForgotForm(true);
                                        setForgotStatus('idle');
                                        setForgotMessage('');
                                    }}
                                >
                                    {t('forgot_password')}
                                </button>
                            </div>

                            {error && <p className="error-msg">{error}</p>}
                        </div>
                    </form>
                </>
            ) : (
                <>
                    <h2>{t('reset_password_heading')}</h2>

                    <form onSubmit={handleForgotPassword}>
                        <input
                            type="email"
                            placeholder={t('email_placeholder')}
                            value={forgotEmail}
                            onChange={(e) => setForgotEmail(e.target.value)}
                            required
                        />

                        <button type="submit" disabled={forgotStatus === 'sending'}>
                            {forgotStatus === 'sending'
                                ? t('sending')
                                : t('send_reset_link')}
                        </button>

                        {forgotMessage && (
                            <p
                                className={
                                    forgotStatus === 'success' ? 'success-msg' : 'error-msg'
                                }
                            >
                                {forgotMessage}
                            </p>
                        )}
                    </form>

                    <button
                        type="button"
                        className="cancel-btn"
                        onClick={() => {
                            setShowForgotForm(false);
                            setForgotEmail('');
                            setForgotStatus('idle');
                            setForgotMessage('');
                        }}
                    >
                        {t('back_to_login')}
                    </button>
                </>
            )}
        </div>
    );
};

export default Login;